import { useContext, useEffect, useState } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Blocks in-app navigations and tab close/refresh while `when` is true.
 * BrowserRouter does not support useBlocker, so Link and navigate() are
 * intercepted on the history navigator. Confirm by calling the returned run().
 */
export default function useUnsavedNavigationGuard(when) {
  const { navigator } = useContext(NavigationContext);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!when) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    const push = navigator.push.bind(navigator);
    const replace = navigator.replace.bind(navigator);
    const go = navigator.go.bind(navigator);

    navigator.push = (...args) => {
      setPending(() => () => push(...args));
    };
    navigator.replace = (...args) => {
      setPending(() => () => replace(...args));
    };
    navigator.go = (delta) => {
      setPending(() => () => go(delta));
    };

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
    };
  }, [when, navigator]);

  const stay = () => setPending(null);

  const confirm = () => {
    const run = pending;
    setPending(null);
    if (typeof run === 'function') run();
  };

  return { open: Boolean(pending), stay, confirm };
}
